// NACE — V1.13.0 (Janik 2026-04-27).
//
// NACE Rev. 2 (Statistische Systematik der Wirtschaftszweige in der EU) —
// oberste Ebene (Abschnitte A bis U). Single source of truth für die Branchen-
// Klassifikation jedes Leads.
//
// Warum:
//   - User-Reklamation 2026-04-27: "wir haben ja schon eine branchenspalte aber
//     ich will das clean haben damit da nicht immer was unterschiedliches ist".
//   - Vorher rendert die Tabelle freitextlich `lead.sector` (Industrie /
//     Schuhhandel / etc.), kommt aus diversen Sonnet-Outputs → inkonsistent.
//   - NACE ist ein geschlossenes Enum (21 Buchstaben). Sonnet MUSS einen davon
//     returnen, sonst Validation-Fail. Ergibt konsistente Filter, Statistiken
//     und ein 1-Letter-Badge in der UI.
//
// Datenmodell-Ansatz:
//   - `lead.classification.naceSection?: NaceSection` (optional, weil Legacy-
//     Leads keine haben). Re-Runs füllen es; Backfill-Script kann's für
//     existierende Leads via inferNaceFromKeywords nachpflegen.
//   - `lead.sector` (frei) bleibt parallel als Sub-Branche/Detail.

import type { SectorTag } from './intent';

export const NACE_SECTION_KEYS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
] as const;

export type NaceSection = (typeof NACE_SECTION_KEYS)[number];

// Volle deutsche Bezeichnungen (offizielles Statistisches Bundesamt).
// Nutzung im Reasoning-Prompt + UI-Tooltip.
export const NACE_SECTIONS: Record<NaceSection, string> = {
  A: 'Land- und Forstwirtschaft, Fischerei',
  B: 'Bergbau und Gewinnung von Steinen und Erden',
  C: 'Verarbeitendes Gewerbe',
  D: 'Energieversorgung',
  E: 'Wasserversorgung; Abwasser- und Abfallentsorgung',
  F: 'Baugewerbe',
  G: 'Handel; Instandhaltung und Reparatur von Kraftfahrzeugen',
  H: 'Verkehr und Lagerei',
  I: 'Gastgewerbe',
  J: 'Information und Kommunikation',
  K: 'Erbringung von Finanz- und Versicherungsdienstleistungen',
  L: 'Grundstücks- und Wohnungswesen',
  M: 'Erbringung von freiberuflichen, wissenschaftlichen und technischen Dienstleistungen',
  N: 'Erbringung von sonstigen wirtschaftlichen Dienstleistungen',
  O: 'Öffentliche Verwaltung, Verteidigung; Sozialversicherung',
  P: 'Erziehung und Unterricht',
  Q: 'Gesundheits- und Sozialwesen',
  R: 'Kunst, Unterhaltung und Erholung',
  S: 'Erbringung von sonstigen Dienstleistungen',
  T: 'Private Haushalte',
  U: 'Exterritoriale Organisationen und Körperschaften',
};

// Kurz-Labels für UI-Badge-Tooltip-Header (1-3 Wörter).
export const NACE_SHORT_LABELS: Record<NaceSection, string> = {
  A: 'Land/Forst',
  B: 'Bergbau',
  C: 'Verarbeitendes Gewerbe',
  D: 'Energie',
  E: 'Wasser/Abfall',
  F: 'Bau',
  G: 'Handel',
  H: 'Verkehr/Lagerei',
  I: 'Gastgewerbe',
  J: 'IT/Medien',
  K: 'Finanz/Versicherung',
  L: 'Immobilien',
  M: 'Freiberuflich/Wissenschaft',
  N: 'Sonst. Dienstleistungen',
  O: 'Öffentliche Verwaltung',
  P: 'Bildung',
  Q: 'Gesundheit/Soziales',
  R: 'Kunst/Unterhaltung',
  S: 'Sonst. Dienstleistungen',
  T: 'Private Haushalte',
  U: 'Exterritorial',
};

// Deterministischer Erst-Pass: Keyword-basiertes Mapping vom freien Branchen-
// Text auf NACE-Section. Catched ~80% der häufigen Fälle ohne Sonnet-Cost.
// Sonnet kann das Ergebnis im Reasoning-Output überstimmen wenn nötig.
//
// Ordering matters: spezifischere Patterns zuerst. Wir matchen das ERSTE
// hit, weil ein "Schuhhersteller" sonst sowohl C (Verarbeitend) als auch
// G (Handel) hitten könnte — wir wollen C als spezifischer.
const KEYWORD_TO_NACE: Array<[RegExp, NaceSection]> = [
  // C — Verarbeitendes Gewerbe (zuerst, bevor Handel-Generika)
  [
    /\b(hersteller|herstellung|produzent|produktion|fabrikant|manufaktur|werk|fabrik|gie(?:ss|ß)erei|schmiede|schreinerei|zimmerei|tischlerei|schlosserei|metallbau|cnc|fertigung|zerspanung|stanzerei|druckerei|bäcker(?:ei)?|konditorei|metzgerei|fleischerei|brauerei|brennerei|kelterei|molkerei|käserei)\b/i,
    'C',
  ],
  // F — Baugewerbe
  [
    /\b(bau|hochbau|tiefbau|rohbau|baufirma|bauunternehm|architekt|bauplanung|sanitär|heizung|elektroinstall|maler|stuckateur|gerüstbau|abbruch|dach(?:decker)?|fliesenleger|bodenleger|trockenbau|pflasterer|garten[\s-]?landschaft|gala[\s-]?bau)\b/i,
    'F',
  ],
  // L — Immobilien. LEA-450: MUST precede G/J/K (specific-first, same rule as C-before-G).
  // Was ordered after K → a property firm whose text also carries a Handel/IT/Finanz token
  // (verkauf / agentur / medien / investment / fonds / leasing / broker / vermögens) was
  // stolen by G/J/K and landed as Handel/IT/Finanz (Imperio Vastgoed → K/J, Om QA). The
  // real-estate vocab is specific enough that this never mis-buckets a genuine retail/IT/
  // finance firm (they don't carry vastgoed/immobili/hausverwaltung/…).
  [
    /\b(immobili\w*|vastgoed|real[\s-]?estate|makler(?!büro)|makelaar\w*|hausverwaltung|property[\s-]?(?:manage|management|manager|letting)|woning\w*|verhuur|wohnungs(?:bau|verwaltung)|projektentwickl\w*|projectontwikkel\w*|grundstücks|landlord|agence[\s-]?immobili|asset[\s-]?management.*immo)\b/i,
    'L',
  ],
  // G — Handel (inkl. Schuhgeschäft, Bekleidung, etc.)
  [
    /\b(handel|händler|geschäft|laden|shop|store|verkauf|einzelhandel|großhandel|gross[\s-]?handel|filial|kfz|autohaus|werkstatt|reparatur|schuh|bekleidung|mode|möbel|elektro(?:nik)?|baumarkt|drogerie|apotheke|supermarkt|markt)\b/i,
    'G',
  ],
  // I — Gastgewerbe
  [
    /\b(hotel|pension|gasthof|gasthaus|restaurant|bistro|café|cafe|kneipe|bar|club|catering|imbiss|fast[\s-]?food|cuisine|gastronomie|herberge)\b/i,
    'I',
  ],
  // J — Information & Kommunikation
  [
    /\b(software|saas|it[\s-]?(?:dienstleist|service|consulting|beratung)?|webdesign|webagentur|app[\s-]?entwickl|programmier|developer|tech[\s-]?startup|agentur|verlag|medien|rundfunk|fernsehen|radio|telekommunikation|telco|internet[\s-]?provider|hosting|cloud|cyber[\s-]?security)\b/i,
    'J',
  ],
  // K — Finanz & Versicherung
  [
    /\b(bank|sparkasse|versicherung|finanz(?:dienst|berater|beratung)|wirtschafts(?:auskunftei|prüfer)|bonität|kredit|leasing|broker|investment|fonds|vermögens(?:verwalter|berater)|maklerbüro|krankenversicherung|rentenversicherung)\b/i,
    'K',
  ],
  // M — Freiberuflich, wissenschaftlich, technisch (Beratung, Anwalt, Steuerberater)
  [
    /\b(beratung|consulting|consult|kanzlei|rechtsanwalt|anwalt|notar|steuerberater|wirtschafts(?:treuhand|prüfung)|ingenieur(?:büro|gesellschaft)|planungsbüro|architekturbüro|forschungs|labor|gutachter|sachverständiger|werbeagentur|marketingagentur|design(?:agentur|büro)?|pr[\s-]?agentur)\b/i,
    'M',
  ],
  // N — Sonstige wirtschaftliche Dienstleistungen (Personaldienstl., Reise, Sicherheit, Reinigung)
  [
    /\b(personal(?:dienst|vermittlung|leasing)|zeitarbeit|leiharbeit|reisebüro|reise(?:veranstalter|büro)|sicherheits(?:dienst|firma)|wach(?:dienst|schutz)|reinigungs(?:firma|service)|gebäudereinigung|hausmeister|gartenpfleger)\b/i,
    'N',
  ],
  // O — Öffentliche Verwaltung
  [
    /\b(rathaus|gemeinde|stadtverwaltung|landratsamt|ministerium|behörde|amt|gericht|polizei|feuerwehr|bundeswehr|finanzamt|sozialamt|jobcenter|stadtwerk(?!e?\s*(?:gmbh|ag)))\b/i,
    'O',
  ],
  // P — Bildung
  [
    /\b(schule|gymnasium|gesamtschule|hauptschule|realschule|grundschule|berufsschule|fachschule|hochschule|universität|fhs|fachhochschule|akademie|bildungs(?:träger|einrichtung)|kindergarten|kita|nachhilfe|fahrschule)\b/i,
    'P',
  ],
  // Q — Gesundheit & Soziales
  [
    /\b(arzt|ärzte|praxis|zahnarzt|kinderarzt|hausarzt|facharzt|kliniken?|krankenhaus|krankenpflege|altenpflege|pflegedienst|pflegeheim|seniorenheim|tagespflege|hebammen?|physiotherap|ergotherap|logopäd|heilpraktiker|tierarzt|tierklinik|sozial(?:dienst|station|werk)|jugendhilfe|caritas|diakonie)\b/i,
    'Q',
  ],
  // R — Kunst, Unterhaltung, Erholung
  [
    /\b(theater|oper|musical|kino|museum|galerie|künstler|atelier|fitnessstudio|fitness|sport(?:verein|club|halle)|wellness|spa|sauna|freizeitpark|zoo|botanischer\s+garten|sportverein|veranstalt|event)\b/i,
    'R',
  ],
  // H — Verkehr & Lagerei
  [
    /\b(spedition|transport|logistik|kurier|paket(?:dienst|service)|umzug|frachtdienst|reederei|fluggesellschaft|airline|taxi|fahrdienst|bus(?:unternehmen|reise)|bahn|lager(?:halt|firma)?|warehous)\b/i,
    'H',
  ],
  // A — Land- & Forstwirtschaft (rare, late check)
  [
    /\b(landwirtschaft|bauern(?:hof)?|gärtnerei(?!\s*(?:gmbh|ag))|forstwirtschaft|fischerei|aquakultur|tierzucht|imkerei|baumschule|saatgut)\b/i,
    'A',
  ],
  // B — Bergbau
  [/\b(bergbau|steinbruch|sandgrube|kies(?:grube|werk)|abbau|förder(?:turm|stätte))\b/i, 'B'],
  // D — Energie (last because broad keywords)
  [
    /\b(energieversorger|stadtwerke|kraftwerk|stromversorger|gasversorger|fernwärme|netzbetreiber|wind(?:park|kraft|energie)|solar(?:park|anlage)?|photovoltaik|geothermie)\b/i,
    'D',
  ],
  // E — Wasser/Abfall
  [
    /\b(wasserwerk|wasserversorgung|abwasser|kläranlage|abfallwirtschaft|recycling|entsorger|müllabfuhr|deponie)\b/i,
    'E',
  ],
];

/**
 * Versuche, einen NACE-Section deterministisch aus freitextlichen Branchen-
 * Hinweisen abzuleiten (businessType + companyDescription + sector).
 *
 * Returns null wenn kein klares Match — dann sollte Sonnet entscheiden.
 */
export function inferNaceFromKeywords(text: string | null | undefined): NaceSection | null {
  if (!text) return null;
  const t = String(text).toLowerCase();
  if (t.trim().length < 3) return null;
  for (const [rx, section] of KEYWORD_TO_NACE) {
    if (rx.test(t)) return section;
  }
  return null;
}

/**
 * Validate that a string is a valid NaceSection key. Used in the orchestrator
 * to coerce Sonnet output: if Sonnet returns "X" (invalid), we drop it and
 * fall back to inferNaceFromKeywords.
 */
export function isValidNaceSection(value: unknown): value is NaceSection {
  return typeof value === 'string' && (NACE_SECTION_KEYS as readonly string[]).includes(value);
}

/**
 * LEA-450: derive the coarse per-lead `SectorTag` from a resolved NACE section, where the
 * section expresses a sector the 6-value tag otherwise can't. Today that's ONLY real-estate
 * (L → 'Immobilien') — the intent parse has no real-estate bucket, so without this a property
 * lead keeps the query's fallback tag ('Handel') and Om's "primarySector = Handel for every
 * landlord" bug persists. Every other section keeps `fallback` (the query's sectorTag) so we
 * don't churn the coarse tag for leads whose query sector is already fine (low blast radius).
 */
export function sectorTagForNace(
  naceSection: NaceSection | null | undefined,
  fallback: SectorTag,
): SectorTag {
  return naceSection === 'L' ? 'Immobilien' : fallback;
}
